/** @jsxImportSource @emotion/react */
import { Button, Container, Modal, Stack, Text, VisuallyHidden } from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { SvgInline } from "components/SvgInline/SvgInline";
import { cardDetailsMap } from "components/WaysToPay/PaymentMethods/DebitCard/cardDetailsMap";
import { getIsPaymentInstrumentExpired } from "components/WaysToPay/PaymentMethods/DebitCard/getIsPaymentInstrumentExpired";
import { PaymentInstrument } from "library/api/payments";

interface DeleteCardModalProps {
  isOpen: boolean;
  instrument?: PaymentInstrument;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteCardModal: React.FC<DeleteCardModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  instrument,
}) => {
  if (!instrument) {
    return null;
  }
  const {
    last_4_digits: last4Digits,
    expiry_date_month: expiryDateMonth,
    expiry_date_year: expiryDateYear,
  } = instrument;

  const cardDetails = cardDetailsMap[instrument.card_scheme];

  if (!cardDetails) {
    return null;
  }

  const isExpired = getIsPaymentInstrumentExpired(instrument);

  const expiresText = `${
    isExpired ? "Expired" : "Expires"
  } ${expiryDateMonth}/${expiryDateYear}`;

  const { iconName, displayName } = cardDetails;

  const labelText = `${displayName} card with last 4 digits ${last4Digits}`;

  return (
    <Modal
      title="Are you sure you want to remove this card?"
      open={isOpen}
      onClose={onClose}
    >
      <Stack gap="4">
        <div
          css={{
            display: "grid",
            gridTemplateColumns: "40px 1fr",
            padding: "15px",
            marginBottom: "4px",
            width: "100%",
            textAlign: "left",
            color: "initial",
            background: isExpired ? "rgba(242, 242, 242, .5)" : "#f2f2f2",
          }}
        >
          <div
            css={{
              gridRow: "1/3",
              alignSelf: "center",
            }}
          >
            <SvgInline name={iconName} />
          </div>
          <div
            css={{
              marginLeft: "16px",
              fontWeight: 500,
            }}
            aria-hidden
          >
            {displayName} | •••• {last4Digits}
          </div>
          <VisuallyHidden>
            <span>{labelText}</span>
          </VisuallyHidden>
          <div
            css={{
              gridRow: 2,
              gridColumn: 2,
              marginLeft: "16px",
              fontSize: "14px",
              color: isExpired ? "#b3000c" : "",
            }}
            aria-hidden
          >
            {expiresText}
          </div>
        </div>
        <Text>
          Any pending payments on this card will be processed even after you
          remove it.
        </Text>
        <Modal.Footer>
          <Container width="100%" width-xs="360">
            <FormActions switchLayoutAt="xs">
              <FormActions.Primary>
                <Button
                  width="100%"
                  variant="primary"
                  onClick={onConfirm}
                  testId="confirm-delete-payment-instrument"
                >
                  Remove card
                </Button>
              </FormActions.Primary>
              <FormActions.Secondary>
                <Button width="100%" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
              </FormActions.Secondary>
            </FormActions>
          </Container>
        </Modal.Footer>
      </Stack>
    </Modal>
  );
};
